/**
 * ====================================================================
 * FIKO CAD RADIUS DIMENSION
 * ====================================================================
 *
 * Yarıçap ölçüsü (R) - Daire, silindir veya yay için.
 * CATIA/IronCAD tarzında profesyonel yarıçap ölçümü.
 *
 * Özellikler:
 * - Merkezden çevre üzerine çizgi
 * - R sembolü
 * - Merkez işareti
 * - Leader line (opsiyonel)
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { FikoSahne } from '../../FikoSahne';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionState,
    GeometryReference,
    DiameterDimensionOptions,
    ArrowStyle,
    DIMENSION_CONSTANTS,
    DimensionErrorType
} from './DimensionTypes';

/**
 * Radius Dimension - Yarıçap ölçüsü
 */
export class RadiusDimension extends Dimension {
    // Referans
    private ref: GeometryReference;

    // Hesaplanmış değerler
    private center: THREE.Vector3 = new THREE.Vector3();
    private radius: number = 0;
    private normal: THREE.Vector3 = new THREE.Vector3(0, 0, 1);
    private surfacePoint: THREE.Vector3 | null = null;  // Eğri yüzeyler için gerçek yüzey noktası

    // Çizim elemanları
    private radiusLine: LineSegments2 | null = null;
    private arrow: THREE.Mesh | THREE.Line | null = null;
    private label: THREE.Sprite | null = null;
    private centerMark: THREE.Group | null = null;

    // Shared geometries for arrow styles
    private static _sharedArrowGeometry: THREE.BufferGeometry | null = null;
    private static _sharedOpenArrowGeometry: THREE.BufferGeometry | null = null;
    private static _sharedDotGeometry: THREE.BufferGeometry | null = null;
    private static _sharedTickGeometry: THREE.BufferGeometry | null = null;

    // Sahne referansı
    private scene: FikoSahne;

    // 🔴 SABİT BOYUT SİSTEMİ - FikoOlcumCizgisi ile aynı pattern
    sabitBoyut = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegistiThrottled();

    // 🚀 PERFORMANS: Smooth scale interpolation için
    private lastKameraUpdateTime: number = 0;
    private kameraUpdateTimeout: number | null = null;
    private static readonly KAMERA_THROTTLE_MS: number = 100;
    private targetScaleAdjustment: number = 1;
    private currentScaleAdjustment: number = 1;
    private interpolationRAF: number | null = null;
    private isDisposed = false;

    constructor(
        ref: GeometryReference,
        options: DiameterDimensionOptions,
        scene: FikoSahne
    ) {
        super(DimensionType.RADIUS, { ...options, prefix: options.prefix || 'R' });

        this.ref = ref;
        this.scene = scene;
    }

    /**
     * Dimension'ı oluştur
     */
    create(): DimensionResult {
        try {
            // Merkez ve yarıçap al
            if (!this.ref.center || !this.ref.radius) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Merkez veya yarıçap tanımlı değil'
                    }
                };
            }

            this.center.copy(this.ref.center);
            this.radius = this.ref.radius;

            if (this.ref.axis) {
                this.normal.copy(this.ref.axis);
            }

            // Eğri yüzeyler için gerçek yüzey noktası
            if (this.ref.surfacePoint) {
                this.surfacePoint = this.ref.surfacePoint.clone();
            }

            // Değeri hesapla
            this._value = this.calculateValue();

            // Validate
            if (!this.validate()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Yarıçap çok küçük'
                    }
                };
            }

            // Geometriyi oluştur
            this.buildGeometry();

            // 🎯 FikoOlcumCizgisi ile uyumlu: Sahneye hazırla (event listener bağla)
            this.hazirla();

            return {
                success: true,
                dimension: this
            };
        } catch (error) {
            return {
                success: false,
                error: {
                    type: DimensionErrorType.INTERNAL_ERROR,
                    message: `Radius dimension oluşturma hatası: ${error}`
                }
            };
        }
    }

    /**
     * Dimension'ı güncelle
     * 🎯 FikoOlcumCizgisi ile uyumlu: Sadece yazı sabit boyut, oklar ve çizgiler normal
     */
    update(): void {
        // 🎯 FikoOlcumCizgisi ile uyumlu: Oklar sabit boyutta kalır
        // Scale güncelleme event listener üzerinden yapılır (kameraYonDegisti)
    }

    /**
     * ✅ Sahneye hazırlama - FikoOlcumCizgisi ile aynı pattern
     */
    hazirla(): void {
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            return;
        }

        try {
            if (this.label && this.label.userData.originalScale) {
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && this.scene.anaGosterim.aktifKontrol) {
                this.scene.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            // Hata durumunda sessizce devam et
        }
    }

    /**
     * 🚀 PERFORMANS: Throttled kamera event handler
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        if (this.kameraUpdateTimeout !== null) {
            return;
        }

        if (now - this.lastKameraUpdateTime < RadiusDimension.KAMERA_THROTTLE_MS) {
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti();
            }, RadiusDimension.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti();
    }

    /**
     * 🔴 YAZININ BOYUTU SABİT - Çizgi normal kalır
     */
    private kameraYonDegisti(): void {
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            return;
        }

        if (this.sabitBoyut && this.label && this.label.userData.originalScale) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;

            this.targetScaleAdjustment = factor * 0.02;
            this.startSmoothInterpolation();
        }
    }

    /**
     * 🎯 SMOOTH: Her frame interpolation
     */
    private startSmoothInterpolation(): void {
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (!this.label || !this.label.userData.originalScale || !this.sabitBoyut) {
                this.interpolationRAF = null;
                return;
            }

            const lerpFactor = 0.2;
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            this.currentScaleAdjustment += diff * lerpFactor;

            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * this.currentScaleAdjustment,
                originalScale.y * this.currentScaleAdjustment,
                1
            );

            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * Validate
     */
    validate(): boolean {
        return this._value >= DIMENSION_CONSTANTS.MIN_RADIUS;
    }

    /**
     * Değeri hesapla
     */
    protected calculateValue(): number {
        return this.radius;
    }

    /**
     * Geometriyi oluştur
     */
    protected buildGeometry(): void {
        this.clearGeometry();

        const options = this._options as DiameterDimensionOptions;
        const color = options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        const lineWidth = options.lineWidth || DIMENSION_CONSTANTS.DEFAULT_LINE_WIDTH;
        const arrowSize = options.arrowSize || DIMENSION_CONSTANTS.DEFAULT_ARROW_SIZE;

        // Çevre üzerindeki nokta ve yarıçap yönü
        let circumferencePoint: THREE.Vector3;
        let radiusDir: THREE.Vector3;

        // Eğri yüzeyler için gerçek yüzey noktasını kullan
        if (this.surfacePoint) {
            circumferencePoint = this.surfacePoint.clone();
            radiusDir = new THREE.Vector3().subVectors(circumferencePoint, this.center).normalize();
        } else {
            // Klasik hesaplama (kameraya göre)
            radiusDir = this.calculateRadiusDirection();
            circumferencePoint = this.center.clone().addScaledVector(radiusDir, this.radius);
        }

        // Yarıçap çizgisi (merkezden çevreye)
        this.radiusLine = this.createLine(
            [
                this.center.x, this.center.y, this.center.z,
                circumferencePoint.x, circumferencePoint.y, circumferencePoint.z
            ],
            color,
            lineWidth
        );
        this.radiusLine.name = 'RadiusLine';
        this.add(this.radiusLine);

        // Ok (çevre üzerinde, merkeze doğru)
        // 🎯 FikoOlcumCizgisi ile uyumlu: Pivot tabanında, ok ucu direkt pozisyon noktasında
        // Pozisyon kaydırması gerekmiyor
        this.arrow = this.createArrow(circumferencePoint.clone(), radiusDir.clone().negate(), color, arrowSize);
        this.arrow.name = 'Arrow';
        this.add(this.arrow);

        // Label (yarıçap çizgisinin ortasında veya dışında)
        const labelPos = this.center.clone().lerpVectors(this.center, circumferencePoint, 0.5);
        const offsetDir = new THREE.Vector3().crossVectors(radiusDir, this.normal).normalize();
        labelPos.addScaledVector(offsetDir, this._options.textOffset || 2);

        this.label = this.createLabel(this.formatValue(this._value), labelPos);
        this.label.name = 'Label';
        this.add(this.label);

        // Merkez işareti
        if (options.showCenterMark !== false) {
            this.centerMark = this.createCenterMark(color, options.centerMarkSize || 2);
            this.add(this.centerMark);
        }
    }

    /**
     * Yarıçap yönünü hesapla (kameraya göre)
     */
    private calculateRadiusDirection(): THREE.Vector3 {
        if (this.scene?.anaGosterim?.aktifKamera) {
            const camera = this.scene.anaGosterim.aktifKamera;

            // Kameradan merkeze yön (projeksiyon)
            const toCamera = new THREE.Vector3()
                .subVectors(camera.position, this.center);

            // Normal düzlemine projeksiyon
            const projected = toCamera.clone()
                .addScaledVector(this.normal, -toCamera.dot(this.normal))
                .normalize();

            if (projected.lengthSq() > 0.01) {
                return projected;
            }
        }

        // Fallback: normal'a dik bir yön
        let radiusDir = new THREE.Vector3(1, 0, 0);
        if (Math.abs(this.normal.dot(radiusDir)) > 0.9) {
            radiusDir.set(0, 1, 0);
        }
        return radiusDir.crossVectors(this.normal, radiusDir).normalize();
    }

    /**
     * Merkez işareti oluştur
     * 🎯 Normal vektörüne göre düzeltilmiş - 3D ortamda doğru pozisyon
     */
    private createCenterMark(color: number, size: number): THREE.Group {
        const group = new THREE.Group();
        group.name = 'CenterMark';

        // 🎯 Normal vektörüne dik iki yön hesapla
        let dir1 = new THREE.Vector3(1, 0, 0);
        if (Math.abs(this.normal.dot(dir1)) > 0.9) {
            dir1.set(0, 1, 0);
        }
        dir1.crossVectors(this.normal, dir1).normalize();
        const dir2 = new THREE.Vector3().crossVectors(this.normal, dir1).normalize();

        // Cross işareti (normal'a dik düzlemde)
        const directions = [dir1, dir2];

        directions.forEach((dir, i) => {
            const p1 = this.center.clone().addScaledVector(dir, -size);
            const p2 = this.center.clone().addScaledVector(dir, size);

            const line = this.createLine(
                [p1.x, p1.y, p1.z, p2.x, p2.y, p2.z],
                color,
                0.5
            );
            line.name = `CenterLine${i + 1}`;
            group.add(line);
        });

        // Küçük daire (normal düzleminde)
        const circleSegments = 16;
        const circleRadius = size * 0.3;
        const positions: number[] = [];

        for (let i = 0; i <= circleSegments; i++) {
            const angle = (i / circleSegments) * Math.PI * 2;
            // Normal düzleminde daire çiz
            const point = this.center.clone()
                .addScaledVector(dir1, Math.cos(angle) * circleRadius)
                .addScaledVector(dir2, Math.sin(angle) * circleRadius);
            positions.push(point.x, point.y, point.z);
        }

        const circleGeom = new LineGeometry();
        circleGeom.setPositions(positions);

        const circleMat = new LineMaterial({
            color,
            linewidth: 0.5,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            fog: false,
            worldUnits: false,
            toneMapped: false
        });

        const circle = new LineSegments2(circleGeom, circleMat);
        circle.renderOrder = Infinity;
        circle.frustumCulled = false;
        circle.computeLineDistances();
        circle.name = 'CenterCircle';
        group.add(circle);

        return group;
    }

    /**
     * Çizgi oluştur
     */
    private createLine(
        positions: number[],
        color: number,
        lineWidth: number,
        opacity: number = 1
    ): LineSegments2 {
        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity,
            fog: false,
            worldUnits: false,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        const line = new LineSegments2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Ok oluştur - ArrowStyle enum desteği
     */
    private createArrow(
        position: THREE.Vector3,
        direction: THREE.Vector3,
        color: number,
        size: number
    ): THREE.Mesh | THREE.Line {
        const arrowStyle = this._options.arrowStyle || ArrowStyle.FILLED;

        // NONE style - görünmez placeholder
        if (arrowStyle === ArrowStyle.NONE) {
            const empty = new THREE.Mesh();
            empty.visible = false;
            return empty;
        }

        const material = new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        let geometry: THREE.BufferGeometry;

        switch (arrowStyle) {
            case ArrowStyle.OPEN:
                // Açık ok - V şeklinde (CATIA tarzı geniş ok)
                if (!RadiusDimension._sharedOpenArrowGeometry) {
                    const shape = new THREE.Shape();
                    shape.moveTo(-0.35, -0.5);
                    shape.lineTo(0, 0.5);
                    shape.lineTo(0.35, -0.5);
                    RadiusDimension._sharedOpenArrowGeometry = new THREE.ShapeGeometry(shape);
                    RadiusDimension._sharedOpenArrowGeometry.rotateX(Math.PI / 2);
                }
                geometry = RadiusDimension._sharedOpenArrowGeometry;
                break;

            case ArrowStyle.DOT:
                // Nokta (görünür boyutta)
                if (!RadiusDimension._sharedDotGeometry) {
                    RadiusDimension._sharedDotGeometry = new THREE.SphereGeometry(0.25, 12, 12);
                }
                geometry = RadiusDimension._sharedDotGeometry;
                break;

            case ArrowStyle.TICK:
                // Çizgi (architectural tick) - 45 derece çapraz çizgi, kalın
                if (!RadiusDimension._sharedTickGeometry) {
                    const tickShape = new THREE.Shape();
                    tickShape.moveTo(-0.35, -0.35);
                    tickShape.lineTo(0.35, 0.35);
                    tickShape.lineTo(0.42, 0.28);
                    tickShape.lineTo(-0.28, -0.42);
                    tickShape.closePath();
                    RadiusDimension._sharedTickGeometry = new THREE.ShapeGeometry(tickShape);
                    RadiusDimension._sharedTickGeometry.rotateX(Math.PI / 2);
                }
                geometry = RadiusDimension._sharedTickGeometry;
                break;

            case ArrowStyle.FILLED:
            default:
                // Dolu ok (CAD standardı) - Geniş ve uzun profesyonel ok
                // 🎯 FikoOlcumCizgisi ile uyumlu: Pivot tabanında, ok ucu pozisyon noktasında
                if (!RadiusDimension._sharedArrowGeometry) {
                    RadiusDimension._sharedArrowGeometry = new THREE.ConeGeometry(0.3, 0.8, 12);
                    // Pivot noktasını cone'nin tabanına taşı (ok ucu pozisyonda olacak)
                    RadiusDimension._sharedArrowGeometry.translate(0, -0.4, 0); // height/2 = 0.8/2 = 0.4
                }
                geometry = RadiusDimension._sharedArrowGeometry;
                break;
        }

        const arrow = new THREE.Mesh(geometry, material);
        arrow.position.copy(position);
        arrow.scale.setScalar(size);

        // 🎯 FikoOlcumCizgisi ile AYNI: Y ekseni yukarı varsayılan, quaternion OKA UYGULANIYOR
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
        arrow.quaternion.copy(quaternion);  // ✅ KRITIK: Quaternion'u oka uygula!

        arrow.renderOrder = Infinity;
        arrow.frustumCulled = false;

        return arrow;
    }

    /**
     * Label oluştur
     */
    private createLabel(text: string, position: THREE.Vector3): THREE.Sprite {
        const options = this._options;
        const fontSize = options.fontSize || DIMENSION_CONSTANTS.DEFAULT_FONT_SIZE;
        const textColor = options.textColor || '#000000';

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize * 4;

        const padding = 16;
        canvas.width = textWidth + padding * 2;
        canvas.height = textHeight + padding * 2;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 4;
        ctx.strokeText(text, canvas.width / 2, canvas.height / 2);

        ctx.fillStyle = textColor;
        ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
        ctx.shadowBlur = 3;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            toneMapped: false
        });

        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        // 🎯 Kompakt yazı scale'i - küçük ve okunabilir
        sprite.scale.set(canvas.width * 0.015, canvas.height * 0.015, 1);
        sprite.renderOrder = Infinity;
        sprite.frustumCulled = false;

        // 🎯 FikoOlcumCizgisi ile uyumlu: Original scale'i sakla (sabit boyut için)
        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);

        return sprite;
    }

    /**
     * Görünümü güncelle
     */
    protected updateAppearance(): void {
        let color: number;

        switch (this._state) {
            case DimensionState.HOVERED:
                color = DIMENSION_CONSTANTS.COLORS.HOVERED;
                break;
            case DimensionState.SELECTED:
                color = DIMENSION_CONSTANTS.COLORS.SELECTED;
                break;
            default:
                color = this._options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        }

        if (this.radiusLine) {
            (this.radiusLine.material as LineMaterial).color.setHex(color);
        }

        if (this.arrow) {
            (this.arrow.material as THREE.MeshBasicMaterial).color.setHex(color);
        }

        if (this.centerMark) {
            this.centerMark.traverse(child => {
                if (child instanceof LineSegments2) {
                    (child.material as LineMaterial).color.setHex(color);
                }
            });
        }
    }

    /**
     * Geometriyi temizle
     */
    private clearGeometry(): void {
        if (this.radiusLine) {
            this.remove(this.radiusLine);
            this.radiusLine.geometry.dispose();
            (this.radiusLine.material as LineMaterial).dispose();
        }

        if (this.arrow) {
            this.remove(this.arrow);
            this.arrow.geometry.dispose();
            (this.arrow.material as THREE.Material).dispose();
        }

        if (this.label) {
            this.remove(this.label);
            this.label.material.map?.dispose();
            this.label.material.dispose();
        }

        if (this.centerMark) {
            this.centerMark.traverse(child => {
                if (child instanceof LineSegments2) {
                    child.geometry.dispose();
                    (child.material as LineMaterial).dispose();
                }
            });
            this.remove(this.centerMark);
        }

        this.radiusLine = null;
        this.arrow = null;
        this.label = null;
        this.centerMark = null;
    }

    /**
     * Dispose - 🎯 FikoOlcumCizgisi ile uyumlu kaynak temizleme
     */
    override dispose(): void {
        if (this.isDisposed) return;
        this.isDisposed = true;

        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        if (this.scene?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        this.clearGeometry();
        super.dispose();
    }
}
